import {
    CompanyData,
    AnalystInfo,
    WeightsResponse,
} from "./DummyWeightInputTab/types";

type WithAnalystWeights = Pick<CompanyData, "analyst_weights">;

export const roundWeight = (weight: number | null | undefined): number => {
    if (weight === null || weight === undefined) return 0;
    // round to 3 decimal places
    return Math.round(weight * 1000) / 1000;
};

// Two-step integer rounding to avoid IEEE 754 drift
export const toWeightPct = (weight: number): number =>
    Math.round(Math.round(weight * 10000) / 10) / 10;

export const formatWeight = (weight: number | null | undefined): string => {
    if (weight === null || weight === undefined) return "";
    return `${toWeightPct(weight).toFixed(1)}%`;
};

export const shortenName = (fullName: string): string => {
    const parts = fullName.trim().split(/\s+/);
    if (parts.length === 1) return parts[0];
    const firstName = parts[0];
    const lastName = parts[parts.length - 1];
    return `${firstName.charAt(0).toUpperCase()}. ${lastName}`;
};

export const getWeightColor = (
    weight: number | null | undefined,
    portfolioWeight: number | null | undefined
): string => {
    const roundedWeight = roundWeight(weight);
    const roundedPortfolio = roundWeight(portfolioWeight);

    if (roundedWeight === roundedPortfolio) return "";
    if (roundedWeight > roundedPortfolio) return "weight-green";
    return "weight-red";
};

const findAnalystWeight = (company: WithAnalystWeights, analystId: string) => {
    if (!Array.isArray(company.analyst_weights)) return undefined;
    return company.analyst_weights.find((aw) => aw.analyst_id === analystId);
};

export const getAnalystWeight = (
    company: WithAnalystWeights,
    analystId: string
): number | null | undefined => {
    const analystWeight = findAnalystWeight(company, analystId);
    if (!analystWeight) return undefined;
    return analystWeight.weight ?? null;
};

export const getAnalystRationale = (
    company: WithAnalystWeights,
    analystId: string
): string | null => findAnalystWeight(company, analystId)?.rationale ?? null;

export const formatWeightForEdit = (
    weight: number | null | undefined
): string => {
    if (weight === null || weight === undefined) return "";
    return parseFloat(toWeightPct(weight).toFixed(1)).toString();
};

export const buildProcessedData = (
    rawData: CompanyData[],
    allAnalysts: AnalystInfo[]
): (CompanyData & Record<string, number | null | undefined>)[] =>
    rawData.map((company) => {
        const row = { ...company } as CompanyData &
            Record<string, number | null | undefined>;
        allAnalysts.forEach((analyst) => {
            row[`analyst_${analyst.analyst_id}`] = getAnalystWeight(
                company,
                analyst.analyst_id
            );
        });
        row.ptf_delta =
            roundWeight(company.team_weight) -
            roundWeight(company.portfolio_weight);
        return row;
    });

export const buildTotalsRow = (
    totals: WeightsResponse["totals"],
    allAnalysts: AnalystInfo[],
    extra: Record<string, string | number | null> = {}
): Record<string, string | number | null> => {
    const row: Record<string, string | number | null> = {
        company_name: "TOTAL",
        portfolio_weight: totals.portfolio_weight,
        team_weight: totals.team_weight,
        ptf_delta:
            roundWeight(totals.team_weight) -
            roundWeight(totals.portfolio_weight),
        ...extra,
    };
    allAnalysts.forEach((analyst) => {
        const analystTotal = totals.analyst_totals.find(
            (at) => at.analyst_id === analyst.analyst_id
        );
        row[`analyst_${analyst.analyst_id}`] = analystTotal?.total ?? 0;
    });
    return row;
};
